import { toast } from 'react-toastify'

import {
  buildChartExportTable,
  type ChartExportColumn,
  type ChartExportTable,
} from 'features/charts-v2/build-chart-export-table'
import type {
  Chart,
  MetricType,
  TimeBucketOption,
} from 'features/charts-v2/types'
import type { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { toCsvString } from 'utils/csv'

const toCsv = (table: ChartExportTable): string =>
  toCsvString(table.headers, table.rows)

/**
 * Create an anchor for the CSV blob and trigger the browser download.
 */
function triggerCsvDownload(csvContent: string, chartTitle: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.setAttribute('href', URL.createObjectURL(blob))
  link.setAttribute('download', `${chartTitle}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Export Charts-2 data as CSV. Builds the row/header table via
 * `buildChartExportTable` (shared with the dashboards-2-1 PPTX appendix),
 * serializes it, and triggers the download.
 *
 * @param data - Chart data from the API (Chart type)
 * @param selectedMetrics - Array of selected metrics
 * @param selectedBreakdowns - Breakdown columns in backend category order
 *   (standard AND custom). Each carries the id used for special-casing and the
 *   header label.
 * @param chartTitle - Title for the CSV file
 * @param timeBucket - Active time bucket; when set, the backend appends the
 *   time-bucket value as the last category dimension, so a Time column is
 *   emitted after the breakdown columns.
 * @param chartType - Active chart type; selects chart-specific table shapes
 *   (the ridgeline exports one row per breakdown value and score bucket).
 */
export async function exportChartAsCSV(
  data: Chart | null,
  selectedMetrics: MetricType[],
  selectedBreakdowns: ChartExportColumn[],
  chartTitle: string = 'Chart',
  timeBucket: TimeBucketOption | null = null,
  chartType?: ChartTypeV2,
): Promise<boolean> {
  if (!data || !data.data || data.data.length === 0) {
    toast.error('No data available to export')
    return false
  }

  try {
    const table = buildChartExportTable({
      chart: data,
      selectedMetrics,
      selectedBreakdowns,
      timeBucket,
      chartType,
    })

    if (!table) {
      toast.error('No data to export')
      return false
    }

    triggerCsvDownload(toCsv(table), chartTitle)

    toast.success('CSV Downloaded')
    return true
  } catch {
    toast.error('Failed to export CSV')
    return false
  }
}
